<style scoped>
tr:nth-child(odd) {
  background: #f6f6f6;
}
</style>
<template>
  <v-container>
    <v-layout row wrap mt-5 pb-3>
      <v-flex xs12>
        <h3>
          <v-icon large color="yellow">shopping_basket</v-icon>
          Basket
        </h3>
        View of all orders you have placed in the basket. You can change
        quantities, delivery options etc.
        <blockquote>
          Please follow our step to step procedure through the basket.
        </blockquote>
      </v-flex>
    </v-layout>
    <v-stepper v-model="step">
      <v-stepper-header>
        <v-stepper-step :complete="step > 1" :editable="step > 1" step="1">
          Basket
        </v-stepper-step>

        <v-divider></v-divider>

        <v-stepper-step :complete="step > 2" :editable="step > 2" step="2">
          Delivery Address
        </v-stepper-step>

        <v-divider></v-divider>

        <v-stepper-step :complete="step > 3" :editable="step > 3" step="3">
          Order Context
        </v-stepper-step>

        <v-divider></v-divider>

        <v-stepper-step :complete="step > 4" :editable="step > 4" step="4">
          Order Options
        </v-stepper-step>

        <v-divider></v-divider>

        <v-stepper-step step="5">Summary</v-stepper-step>
      </v-stepper-header>

      <v-stepper-items>
        <v-stepper-content height="auto" step="1">
          <v-layout pa-0>
            <basket-item-list mutable></basket-item-list>
          </v-layout>
          <v-layout row justify-end pa-4>
            <v-btn color="primary mx-0" @click="goTo(2)" :disabled="!itemCount">
              Continue
              <v-icon small class="pl-2">chevron_right</v-icon>
            </v-btn>
          </v-layout>
        </v-stepper-content>

        <v-stepper-content
          v-if="!!basketId"
          class="mb-5"
          height="auto"
          step="2"
        >
          <v-layout pb-4>
            <address-list
              collection="shipTos"
              :items="shipTos"
              :selected-id="getShipToId"
              :allowAddAdress="canAddShipTo"
              @select="setShipTo"
              @onSuccessAddAddress="onAddShipToSuccess"
              selectable
              editable
            ></address-list>
          </v-layout>
          <v-layout row justify-space-between pr-3 pl-3>
            <v-btn class="mr-0" outline color="grey" dark @click="goTo(1)">
              <v-icon small class="pr-2">chevron_left</v-icon>
              Back
            </v-btn>
            <v-btn color="primary" :disabled="!getShipToId" @click="goTo(3)">
              Continue
              <v-icon small class="pl-2">chevron_right</v-icon>
            </v-btn>
          </v-layout>
        </v-stepper-content>

        <v-stepper-content
          v-if="!!basketId"
          class="mb-5"
          height="auto"
          step="3"
        >
          <basket-order-context
            @back="goTo(2)"
            @continue="goTo(4)"
          ></basket-order-context>
        </v-stepper-content>

        <v-stepper-content v-if="!!basketId" height="auto" step="4">
          <basket-order-options
            @back="goTo(3)"
            @continue="goTo(5)"
          ></basket-order-options>
        </v-stepper-content>

        <v-stepper-content
          v-if="!!basketId"
          class="mb-5"
          height="auto"
          step="5"
        >
          <basket-item-list row wrap></basket-item-list>
          <basket-summary row wrap justify-space-between></basket-summary>
          <v-layout row pr-3 pl-3>
            <v-checkbox v-model="agreement" color="primary" value="1" required>
              <template ma-0 pa-0 v-slot:label>
                I agree to the Evident&nbsp;
                <a
                  href="https://www.olympus-europa.com/corporate/rmt/media/corporate_1/about_olympus/downloads/agb_neu_2014/OLY_EN_terms_of_purchase.pdf"
                  target="_blank"
                >
                  terms and conditions
                </a>
                .*
              </template>
            </v-checkbox>
          </v-layout>
          <v-layout row justify-space-between pr-3 pl-3>
            <v-btn class="mr-0" outline color="grey" dark @click="goTo(4)">
              <v-icon small class="pr-2">chevron_left</v-icon>
              Back
            </v-btn>
            <v-btn
              :disabled="loading || !agreement"
              :loading="sending"
              color="primary mx-0"
              @click="placeOrder()"
            >
              Place order
            </v-btn>
          </v-layout>
        </v-stepper-content>
      </v-stepper-items>
    </v-stepper>
  </v-container>
</template>

<script>
import { mapGetters, mapMutations, mapState } from "vuex";
import AddressList from "@/components/AddressList.vue";
import BasketItemList from "@/components/BasketItemList.vue";
import BasketOrderContext from "@/components/BasketOrderContext.vue";
import BasketOrderOptions from "@/components/BasketOrderOptions.vue";
import BasketSummary from "@/components/BasketSummary.vue";

export default {
  components: {
    AddressList,
    BasketItemList,
    BasketOrderContext,
    BasketOrderOptions,
    BasketSummary
  },
  data() {
    return {
      step: 1,
      agreement: false
    };
  },
  computed: {
    ...mapGetters("basket", ["itemCount", "getShipToId"]),
    ...mapState("basket", ["basketId", "loading", "sending"]),
    ...mapGetters("customer", {
      shipTos: "getShipTos",
      showPrice: "canSeePrice",
      canAddShipTo: "canAddShipTo"
    })
  },
  methods: {
    ...mapMutations("basket", ["setLoading"]),
    setShipTo(value) {
      if (value.id && value.id == this.getShipToId) {
        this.$store.dispatch("basket/unsetShipTo");
      } else {
        this.$store.dispatch("basket/setShipTo", value);
      }
    },
    goTo(step) {
      this.step = step;

      switch (step) {
        case 5:
          if (this.showPrice) {
            this.setLoading(true);
            this.$store.dispatch("basket/simulateOrder").then(() => {
              this.setLoading(false);
            });
          }
          break;

        default:
          break;
      }
    },
    placeOrder() {
      this.setLoading(true);
      this.$store.dispatch("basket/placeOrder").then(() => {
        this.setLoading(false);
      });
    },
    onAddShipToSuccess(shipTo) {
      this.$store.dispatch("basket/setShipTo", shipTo);
    }
  }
};
</script>
